import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Alert } from 'reactstrap'
import { loadMicroFrontend } from './microFrontendLoader'
import type { MicroFrontendProps, PostMessageEvent } from './types'

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

const MicroFrontendContainer: React.FC<MicroFrontendProps> = ({
  url,
  language,
  onFormSubmit,
  onError
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  
  // Load micro-frontend on mount (after container is rendered)
  // Note: Always uses PROD mode (manifest.json) because Vite HMR can't work cross-origin
  useEffect(() => {
    console.log('✅ Container mounted, loading micro-frontend from:', url)
    
    loadMicroFrontend(url)
      .then(() => {
        console.log('✅ Micro-frontend script loaded')
        setLoaded(true)
      })
      .catch((err) => {
        console.error('❌ Failed to load micro-frontend:', err)
        setError(err)
        onError?.(err)
      })
  }, [url, onError])
  
  // Listen for postMessage events
  useEffect(() => {
    const handleMessage = (event: MessageEvent<PostMessageEvent>) => {
      // Security: Validate origin
      // Note: In script injection mode (non-iframe), micro-frontend shares same window
      // so origin will be the host's origin (localhost:8000 in dev)
      const allowedOrigins = [
        'http://localhost:3000',      // Mock API server
        'http://localhost:3001',      // Micro-frontend preview server
        'http://localhost:5173',      // Micro-frontend dev server (standalone)
        'http://localhost:8000',      // Gatsby dev server (default)
        'http://localhost:9000',      // Gatsby dev server (alternate)
        'https://torolalana.gov.mg',  // Production host
        'https://certificat-residence.app.digital.gov.mg',  // Production micro-frontend
        'https://staging.certificat-residence.app.digital.gov.mg'
      ]

      if (!allowedOrigins.includes(event.origin)) {
        console.warn('Message from unauthorized origin:', event.origin)
        return
      }
      
      // Handle different message types
      // Note: HEIGHT_CHANGED disabled - causes feedback loop with script injection
      // (micro-frontend shares same DOM, height adjusts naturally)
      switch (event.data.type) {
        case 'FORM_SUBMITTED':
          onFormSubmit?.(event.data.payload)
          break
          
        case 'ERROR':
          const err = new Error(event.data.payload.message)
          setError(err)
          onError?.(err)
          break
          
        case 'READY':
          // Micro-frontend is ready, send initial language
          sendMessageToMicroFrontend({
            type: 'LANGUAGE_CHANGED',
            payload: { language }
          })
          break
      }
    }
    
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [language, onFormSubmit, onError])
  
  // Send language updates to micro-frontend
  useEffect(() => {
    if (loaded) {
      sendMessageToMicroFrontend({
        type: 'LANGUAGE_CHANGED',
        payload: { language }
      })
    }
  }, [language, loaded])
  
  const sendMessageToMicroFrontend = (message: PostMessageEvent) => {
    // In script injection mode, micro-frontend shares same window
    // so we post to same origin
    const targetOrigin = window.location.origin

    console.log('📤 Host sending message to micro-frontend:', message)
    window.postMessage(message, targetOrigin)
  }
  
  // Error state
  if (error) {
    return (
      <Alert color="warning">
        <h4>Formulaire temporairement indisponible</h4>
        <p>
          Le formulaire de demande de certificat de résidence est temporairement
          indisponible. Veuillez réessayer plus tard ou contacter le support.
        </p>
        <button 
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Réessayer
        </button>
      </Alert>
    )
  }
  
  // Loading state
  if (!loaded) {
    return (
      <LoadingSpinner>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </LoadingSpinner>
    )
  }
  
  // Render micro-frontend mount point
  return (
    <Container ref={containerRef}>
      <div id="certificat-residence-root" data-testid="micro-frontend-root" />
    </Container>
  )
}

export default MicroFrontendContainer

